import React, { useEffect } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSchemaProps {
  faqs: FAQItem[];
}

/**
 * FAQSchema Component
 * Injects FAQ structured data for Google's "People also ask" section
 * https://developers.google.com/search/docs/appearance/structured-data/faqpage
 */
export default function FAQSchema({ faqs }: FAQSchemaProps) {
  useEffect(() => {
    // Remove any existing FAQ schemas first to prevent duplicates
    const existingSchemas = document.querySelectorAll('script[type="application/ld+json"]');
    existingSchemas.forEach(script => {
      try {
        const content = JSON.parse(script.textContent || '');
        if (content['@type'] === 'FAQPage') {
          script.remove();
        }
      } catch (e) {
        // Ignore invalid JSON
      }
    });

    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    const scriptId = 'faq-schema';
    const scriptTag = document.createElement('script');
    scriptTag.id = scriptId;
    scriptTag.type = 'application/ld+json';
    scriptTag.textContent = JSON.stringify(schema);
    document.head.appendChild(scriptTag);

    return () => {
      const tag = document.getElementById(scriptId);
      if (tag) {
        tag.remove();
      }
    };
  }, [faqs]);

  return null;
}

// Common FAQ sets for reuse across pages
export const commercialPaintingFAQs: FAQItem[] = [
  {
    question: "How long does commercial painting take?",
    answer: "Commercial painting timelines vary based on project size and scope. A typical office space (2,000-5,000 sq ft) takes 3-7 days, while larger commercial buildings may require 2-4 weeks. We work efficiently to minimize business disruption, often scheduling work during evenings, weekends, or off-hours. Danis Painting provides detailed project timelines during the free estimate process."
  },
  {
    question: "What paint brands do you use for commercial projects?",
    answer: "Danis Painting uses premium commercial-grade paints from industry leaders including Sherwin-Williams Pro Industrial, Benjamin Moore Regal Select, and Dunn-Edwards SPARTACOTE. We select paints based on your specific needs—high-traffic durability, low-VOC requirements, or specialized coatings for industrial environments. All products meet California environmental standards and provide long-lasting protection."
  },
  {
    question: "Do you work around our business hours?",
    answer: "Yes! Danis Painting specializes in flexible scheduling to minimize disruption to your operations. We offer evening, weekend, and holiday painting services for commercial clients. Our team can work in phases, completing one section at a time so your business remains operational. We've successfully completed projects for retail stores, restaurants, offices, and warehouses while they stayed open."
  },
  {
    question: "How much does commercial painting cost?",
    answer: "Commercial painting costs typically range from $2-$6 per square foot depending on surface preparation, paint quality, building height, and project complexity. A 5,000 sq ft office might cost $10,000-$30,000, while larger industrial facilities vary significantly. Danis Painting provides free, detailed estimates with transparent pricing—no hidden fees. Contact us at 805-403-8727 for a customized quote."
  },
  {
    question: "Do you provide warranties on commercial painting?",
    answer: "Yes, Danis Painting offers comprehensive warranties on all commercial projects. Our standard warranty covers workmanship for 2 years and paint performance for 5-10 years depending on the product used. We use premium paints with manufacturer warranties and stand behind our craftsmanship. With over 30 years of experience, we ensure every project meets the highest quality standards."
  },
  {
    question: "Can you paint during our busy season?",
    answer: "Absolutely. Danis Painting works year-round and can schedule your commercial painting project during your slow season or off-hours during busy periods. We understand business cycles and will coordinate with your schedule. For retail clients, we often work overnight or after closing. For restaurants, we schedule between services or during closure days. Our goal is zero disruption to your revenue."
  },
  {
    question: "What preparation is included in commercial painting?",
    answer: "Danis Painting includes comprehensive surface preparation: power washing, scraping loose paint, filling cracks and holes, sanding rough surfaces, priming bare areas, and protecting fixtures/furniture. For commercial projects, we also handle drywall repair, texture matching, and specialized prep for metal, concrete, or stucco surfaces. Proper preparation ensures paint adhesion and longevity—we never skip this critical step."
  },
  {
    question: "Are your painters licensed and insured?",
    answer: "Yes, Danis Painting is fully licensed (California Contractor License), bonded, and insured with general liability and workers' compensation coverage. All team members are experienced professionals who undergo background checks. We provide certificates of insurance upon request and ensure compliance with OSHA safety standards. Your property and business are fully protected throughout the project."
  }
];

export const residentialPaintingFAQs: FAQItem[] = [
  {
    question: "How do you prepare surfaces before painting?",
    answer: "Surface preparation is critical for lasting results. We thoroughly clean all surfaces, scrape loose paint, sand rough areas, fill cracks and holes with spackling compound, caulk gaps around trim and windows, prime bare wood and patched areas, and protect floors and furniture with drop cloths. Proper preparation ensures paint adheres correctly and lasts for years."
  },
  {
    question: "How long does interior painting take?",
    answer: "A typical 2,000 sq ft home interior takes 3-5 days depending on the number of rooms, ceiling height, and color changes. We work efficiently while maintaining quality. Dark-to-light color changes may require additional coats and time. We provide a detailed timeline in your estimate and communicate any adjustments during the project."
  },
  {
    question: "Do I need to move all my furniture?",
    answer: "We can work around most furniture. For best results, we recommend clearing small items, wall decorations, and breakables. Our team will carefully move larger furniture to the center of rooms and cover everything with protective cloths. If you prefer, we can arrange professional furniture moving services for an additional fee."
  },
  {
    question: "What paint brands do you recommend?",
    answer: "We use premium paints from Sherwin-Williams, Benjamin Moore, and Dunn-Edwards. For interiors, we recommend Sherwin-Williams Emerald or Benjamin Moore Aura for superior coverage and durability. For exteriors, we use weather-resistant formulas like Duration or Aura Exterior. All products are low-VOC for indoor air quality and come with manufacturer warranties."
  },
  {
    question: "How long before I can use the room after painting?",
    answer: "With modern low-VOC paints, rooms are safe to occupy within 2-4 hours after painting. However, we recommend waiting 24 hours before moving furniture back to allow paint to fully cure. Avoid washing painted walls for at least 2 weeks. Full cure time is 30 days, after which the paint reaches maximum durability and can be cleaned normally."
  },
  {
    question: "Do you offer a warranty on your painting work?",
    answer: "Yes, we provide a 3-year warranty on residential painting covering workmanship defects including peeling, cracking, or adhesion issues under normal conditions. We also honor manufacturer warranties on paint products (typically 10-25 years depending on the product). Our warranty demonstrates our confidence in quality workmanship and premium materials."
  }
];
